// file path: src\services\common.functions.ts

import moment from "moment/moment";

export function secondsToHHMM(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const hoursString = hours.toString().padStart(2, '0');
    const minutesString = minutes.toString().padStart(2, '0');

    return `${hoursString}:${minutesString}`;
}

export function hoursToString(decimalHours: number): string {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    const paddedMinutes = minutes > 0 ? ":" + minutes.toString().padStart(2, '0') : "";
    const paddedHours = hours.toString().padStart(2, '0');
    return `${paddedHours}${paddedMinutes}`;
}

export function hoursToString2(decimalHours: number): string {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    const components: string[] = [];
    if (hours > 0) components.push(`${hours}h`);
    if (minutes > 0) components.push(`${minutes}m`);
    return components.join(" ");
}

export function stringToHours(timeString: string) {
    timeString = timeString.replace("h ", ":").replace("m", "");
    const [hours = 0, minutes = 0] = timeString.split(':').map((item) => parseFloat(item) || 0);
    const number = hours + (minutes / 60);
    return number;
}

export function generateRandomPassword(length = 12): string {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
    let password = "";
    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * charset.length);
        password += charset[randomIndex];
    }
    return password;
}

export function parseFloatFromStr(str: string): number | string {
    const floatData = parseFloat(str);
    return floatData > 0 ? floatData.toFixed(2) : "0";
}

export const dateFormat = {
    fShort:'MMM DD',
    fLong:'DD MMM YY',
    short: (dateStr: string): string => moment(dateStr).format(dateFormat.fShort),
    long: (dateStr: string): string => moment(dateStr).format(dateFormat.fLong),
};

export const dateTimeFormat = {
    short: (timeStr: string): string => moment(timeStr).format('MMM DD, hh:mm A'),
    long: (timeStr: string): string => moment(timeStr).format('MMM DD, YYYY - hh:mm A'),
};

export function isPastDate(inputDate: string): boolean {
    const dateToCheck = moment(inputDate);
    return dateToCheck.isBefore(moment(), 'day');
}

export function isPastDateTime(inputDateTime: string): boolean {
    const dateTimeToCheck = moment(inputDateTime);
    return dateTimeToCheck.isBefore(moment());
}

interface EnhanceOptions {
    focusInput?: boolean;
    glow?: boolean;
    startAfter?: number;
    stopAfter?: number;
}

export const enhanceElementAppearance = (selector: string, options: EnhanceOptions = {}): void => {
    const { focusInput = false, glow = true, startAfter = 10, stopAfter = 2000 } = options;

    setTimeout(() => {
        const element = document.querySelector(selector);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (glow) {
                element.classList.add('glow-effect');
                setTimeout(() => {
                    element.classList.remove('glow-effect');
                }, stopAfter);
            }
            if (focusInput) {
                element.querySelector('input')?.focus();
            }
        }
    }, startAfter);
};

export const formatTimeSpent = (value: string): string => {
    if (!value) return '0h 0m';

    const parts = value.split(/[:hHmM]+/);
    let hours = parts.length > 1 ? parseInt(parts[0], 10) : 0;
    let minutes = parts.length > 1 ? parseInt(parts[1], 10) : parseInt(parts[0], 10);

    if (isNaN(hours)) hours = 0;
    if (isNaN(minutes)) minutes = 0;

    hours += Math.floor(minutes / 60);
    minutes = minutes % 60;

    return `${hours}h ${minutes}m`;
};

function insertCharFromEnd(str: string, char: string, posFromEnd: number): string {
    const posFromStart = str.length - posFromEnd;

    if (posFromStart < 0 || posFromStart > str.length) {
        return 'Position out of bounds';
    }

    return str.slice(0, posFromStart) + char + str.slice(posFromStart);
}

export const parseInput = (value: string): string => {
    let numbers = value.replace(/\D/g, '');
    if (numbers.length > 4) numbers = numbers.slice(-4);

    let minutes = numbers.slice(-2);
    let hours = numbers.slice(0, -2) || '0';

    return `${hours}:${minutes.padStart(2, '0')}`;
};

export function truncateWithEllipsis(s: string, maxLength: number): string {
    let result = s.substring(0, maxLength);
    if (s.length > maxLength) {
        result += '...';
    }
    return result;
}
/**
 * Moves an element in an array from one position to another.
 *
 * @param arr - The array to modify. It can be an array of any type.
 * @param fromIndex - The index of the element to move.
 * @param toIndex - The index to move the element to.
 * @returns A new array with the element moved.
 *
 * @template T - The type of the elements in the array (could be number, string, etc.).
 */
export function moveElement<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
    // Ensure the fromIndex is within bounds
    if (fromIndex < 0 || fromIndex >= arr.length) {
        throw new Error("fromIndex is out of bounds");
    }

    // Ensure the toIndex is within bounds
    if (toIndex < 0 || toIndex >= arr.length) {
        throw new Error("toIndex is out of bounds");
    }

    // Remove the element from the "fromIndex"
    const element = arr.splice(fromIndex, 1)[0];

    // Insert the element at the "toIndex"
    arr.splice(toIndex, 0, element);

    return arr;
}
